import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { apiRequest } from '../utils/apiHelper';

import { useBranding } from '../context/BrandingContext';
import '../styles/login.css';
import { showSuccess, showError } from '../utils/toastHelper';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { branding, loading: brandingLoading } = useBranding();

  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [formError, setFormError] = useState('');

  const hasMinLength = formData.newPassword.length >= 8;
  const hasUpper = /[A-Z]/.test(formData.newPassword);
  const hasLower = /[a-z]/.test(formData.newPassword);
  const hasNumber = /\d/.test(formData.newPassword);
  const isPasswordValid = hasMinLength && hasUpper && hasLower && hasNumber;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (formData.newPassword !== formData.confirmPassword) {
      setFormError('Şifreler eşleşmiyor.');
      return;
    }

    if (!isPasswordValid) {
      setFormError('Lütfen tüm şifre kurallarını karşıladığınızdan emin olun.');
      return;
    }

    setLoading(true);

    try {
      await apiRequest('/auth/reset-password', {
        method: 'POST',
        data: {
          token,
          newPassword: formData.newPassword
        },
        skipAuth: true
      });

      showSuccess('Şifreniz başarıyla güncellendi! Giriş yapabilirsiniz.');
      navigate('/login');
    } catch (error) {
      console.error('Password reset error:', error);
      setFormError(error.message || 'Şifre sıfırlama başarısız. Linkin süresi dolmuş olabilir.');
    } finally {
      setLoading(false);
    }
  };

  if (brandingLoading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: '#f8f9fc' }}>
        <div className="spinner-border" style={{ color: '#aaa' }} role="status" />
      </div>
    );
  }

  const companyName = branding.name || '';
  const logoSrc = branding.logoUrl || branding.companyLogo || null;
  const accent = branding.brandColor || '#1e293b';
  const accentSoft = accent + '18';
  const heroTitle = branding.heroTitle || '';
  const heroSub = branding.heroSubtitle || branding.tagline || '';

  if (!token) {
    return (
      <div className="login-page">
        <div className="login-form-panel w-100 d-flex align-items-center justify-content-center">
          <div className="text-center p-5 rounded-4 shadow-sm bg-white" style={{ maxWidth: '400px' }}>
            <i className="fas fa-exclamation-circle text-danger fa-4x mb-4"></i>
            <h4 className="fw-bold mb-3">Geçersiz Bağlantı</h4>
            <p className="text-muted mb-4">
              Şifre sıfırlama jetonu bulunamadı. Lütfen e-postanızdaki linke tıkladığınızdan emin olun veya tekrar istek gönderin.
            </p>
            <Link to="/forgot-password" className="login-submit text-decoration-none" style={{ '--accent': accent }}>
              Tekrar İstek Gönder
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      {/* ── Left: Branded Panel ─────────────────────────────── */}
      <div className="login-brand-panel" style={{ '--accent': accent, '--accent-soft': accentSoft }}>
        <div className="login-brand-bg" />
        <div className="login-brand-content">
          {logoSrc && (
            <img src={logoSrc} alt={companyName || 'Logo'} className="login-brand-logo" />
          )}
          {heroTitle && <h1 className="login-brand-title">{heroTitle}</h1>}
          {heroSub && <p className="login-brand-subtitle">{heroSub}</p>}
          {!logoSrc && !heroTitle && companyName && companyName !== 'System Admin Company' && (
            <h1 className="login-brand-title">{companyName}</h1>
          )}
        </div>
      </div>

      {/* ── Right: Form ─────────────────────────────────────── */}
      <div className="login-form-panel">
        <div className="login-form-wrapper">
          {/* Mobile-only header */}
          <div className="login-mobile-header" style={{ '--accent': accent }}>
            {logoSrc ? (
              <img src={logoSrc} alt={companyName || 'Logo'} className="login-mobile-logo" />
            ) : companyName && companyName !== 'System Admin Company' ? (
              <span className="login-mobile-name" style={{ color: accent }}>{companyName}</span>
            ) : null}
          </div>

          <div className="login-form-inner">
            <h2 className="login-form-title">Yeni Şifre Belirle</h2>
            <p className="login-form-desc">
              Lütfen hesabınız için yeni ve güvenli bir şifre oluşturun.
            </p>

            {formError && (
              <div className="alert alert-danger d-flex align-items-center mb-4" role="alert" style={{ fontSize: '0.875rem', borderRadius: '10px', backgroundColor: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca' }}>
                <i className="fas fa-exclamation-circle me-2" style={{ fontSize: '1.2rem' }} />
                <div>{formError}</div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="login-form mt-4">
              <div className="login-field">
                <label className="login-label" htmlFor="newPassword">Yeni Şifre</label>
                <div className="login-input-wrap" style={{ '--accent': accent }}>
                  <i className="fas fa-lock login-input-icon" />
                  <input
                    id="newPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    name="newPassword"
                    className="login-input"
                    value={formData.newPassword}
                    onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                    required
                    disabled={loading}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    className="login-eye-btn"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    tabIndex={-1}
                  >
                    <i className={`fas fa-${showNewPassword ? 'eye-slash' : 'eye'}`} />
                  </button>
                </div>
                
                {/* Password Hints */}
                <div className="mt-2" style={{ fontSize: '0.8rem' }}>
                  <div className={`d-flex align-items-center ${hasMinLength ? 'text-success' : 'text-muted'}`}>
                    <i className={`fas ${hasMinLength ? 'fa-check-circle' : 'fa-circle'} me-2`} style={{ fontSize: '0.6rem' }} /> En az 8 karakter
                  </div>
                  <div className={`d-flex align-items-center ${hasUpper ? 'text-success' : 'text-muted'}`}>
                    <i className={`fas ${hasUpper ? 'fa-check-circle' : 'fa-circle'} me-2`} style={{ fontSize: '0.6rem' }} /> En az 1 büyük harf
                  </div>
                  <div className={`d-flex align-items-center ${hasLower ? 'text-success' : 'text-muted'}`}>
                    <i className={`fas ${hasLower ? 'fa-check-circle' : 'fa-circle'} me-2`} style={{ fontSize: '0.6rem' }} /> En az 1 küçük harf
                  </div>
                  <div className={`d-flex align-items-center ${hasNumber ? 'text-success' : 'text-muted'}`}>
                    <i className={`fas ${hasNumber ? 'fa-check-circle' : 'fa-circle'} me-2`} style={{ fontSize: '0.6rem' }} /> En az 1 rakam
                  </div>
                </div>
              </div>

              <div className="login-field mt-3">
                <label className="login-label" htmlFor="confirmPassword">Yeni Şifre (Tekrar)</label>
                <div className="login-input-wrap" style={{ '--accent': accent }}>
                  <i className="fas fa-check-double login-input-icon" />
                  <input
                    id="confirmPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    className="login-input"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    required
                    minLength="8"
                    disabled={loading}
                    placeholder="••••••••"
                  />
                </div>
              </div>

              <button
                type="submit"
                className="login-submit mt-4"
                style={{ '--accent': accent }}
                disabled={loading}
              >
                {loading ? (
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                ) : (
                  <i className="fas fa-save me-2"></i>
                )}
                Şifreyi Güncelle
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
